import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  type BaseMultiSelectOption,
  DisplayMode,
  ThemedMultiselectDropdown,
} from "@vega-core/components/Form/Controls/Dropdown/ThemedMultiselectDropdown/ThemedMultiselectDropdown";
import { Tooltip } from "@vega-core/components/Tooltip/Tooltip";
import { m } from "@vega-core/paraglide/messages";

import { TooltipErrorContent } from "./TooltipErrorContent";

type MultiselectWithTooltipErrorProps = {
  name: string;
  error?: string;
  selectedOptions: BaseMultiSelectOption[];
  onSetSelectedOptions: (options: BaseMultiSelectOption[]) => void;
  options: BaseMultiSelectOption[];
  tableRef?: RefObject<HTMLDivElement | null>;
  dropdownRef?: RefObject<HTMLDivElement>;
};

export const MultiselectWithTooltipError = ({
  name,
  error,
  selectedOptions,
  onSetSelectedOptions,
  options,
  tableRef,
  dropdownRef,
}: MultiselectWithTooltipErrorProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const internalWrapperRef = useRef<HTMLDivElement>(null);
  const wrapperRef = dropdownRef || internalWrapperRef;
  const hasError = !!error;
  const showTooltip = isHovered && hasError && !isPopoverOpen && !isScrolling;

  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) return;

    const button = wrapper.querySelector("button");

    if (!button) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "aria-expanded") {
          const isOpen = button.getAttribute("aria-expanded") === "true";
          setIsPopoverOpen(isOpen);
        }
      });
    });

    observer.observe(button, {
      attributes: true,
      attributeFilter: ["aria-expanded"],
    });

    return () => observer.disconnect();
  }, [wrapperRef]);

  const handleFocus = useCallback(() => {
    setIsScrolling(false);

    if (tableRef?.current) {
      const handleScroll = () => {
        setIsScrolling(true);
      };
      tableRef.current.addEventListener("scroll", handleScroll, { once: true });
    }
  }, [tableRef]);

  const tooltipContent = hasError ? (
    <TooltipErrorContent error={error} />
  ) : null;

  return (
    <Tooltip
      className="max-w-72"
      content={tooltipContent}
      contentProps={{
        side: "top",
        align: "start",
      }}
      providerProps={{ delayDuration: 0 }}
      rootProps={{ open: showTooltip }}
    >
      <div
        className={hasError ? "[&_button]:!border-border/negative" : ""}
        onFocus={handleFocus}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        ref={wrapperRef}
      >
        <ThemedMultiselectDropdown
          displayMode={DisplayMode.Tags}
          hideSelectAllOption
          name={name}
          options={options}
          placeholder={m.coInvestments_reasonForInclusion()}
          searchEnabled={false}
          selectedOptions={selectedOptions}
          setSelectedOptions={onSetSelectedOptions}
        />
      </div>
    </Tooltip>
  );
};
